/**
 * Personenakten - Natürliche und Juristische Personen verwalten
 * Vernetzte Datenbank mit Maßnahmen
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserCircle, 
  Building2, 
  Plus, 
  Search,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Link,
  RefreshCw,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
// Dialog imports removed - all forms are now inline
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';

// Rollen für natürliche Personen (strukturiert)
const ROLLEN = [
  { value: 'alle', label: 'Alle Rollen' },
  { value: 'richter', label: 'Richter', kat: 'Betreuungsgericht' },
  { value: 'rechtspfleger', label: 'Rechtspfleger', kat: 'Betreuungsgericht' },
  { value: 'sachbearbeiter', label: 'Sachbearbeiter/in', kat: 'Behörde' },
  { value: 'ansprechpartner_bank', label: 'Ansprechpartner Bank', kat: 'Bank' },
  { value: 'hausarzt', label: 'Hausarzt', kat: 'Arzt/Therapie' },
  { value: 'facharzt', label: 'Facharzt', kat: 'Arzt/Therapie' },
  { value: 'therapeut', label: 'Therapeut', kat: 'Arzt/Therapie' },
  { value: 'heimleitung', label: 'Heimleitung', kat: 'Pflege/Heim' },
  { value: 'pflegeperson', label: 'Pflegeperson', kat: 'Pflege/Heim' },
  { value: 'sozialarbeiter', label: 'Sozialarbeiter/in', kat: 'Soziales' },
  { value: 'gesetzlicher_betreuer', label: 'Gesetzlicher Betreuer', kat: 'Soziales' },
  { value: 'angehoeriger', label: 'Angehöriger', kat: 'Familie' },
  { value: 'anwalt', label: 'Anwalt', kat: 'Recht' },
  { value: 'notar', label: 'Notar', kat: 'Recht' },
  { value: 'steuerberater', label: 'Steuerberater', kat: 'Recht' },
  { value: 'kontaktperson', label: 'Kontaktperson', kat: 'Sonstiges' },
];

// Typen für juristische Personen
const TYPEN = [
  { value: 'alle', label: 'Alle Typen' },
  { value: 'gericht', label: 'Gericht' },
  { value: 'behoerde', label: 'Behörde' },
  { value: 'krankenversicherung', label: 'Krankenversicherung' },
  { value: 'pflegeheim', label: 'Pflegeheim' },
  { value: 'bank', label: 'Bank' },
  { value: 'versicherung', label: 'Versicherung' },
  { value: 'pflegedienst', label: 'Pflegedienst' },
  { value: 'arztpraxis', label: 'Arztpraxis' },
  { value: 'rechtsanwalt', label: 'Rechtsanwalt' },
  { value: 'arbeitgeber', label: 'Arbeitgeber' },
  { value: 'vermieter', label: 'Vermieter' },
  { value: 'glaeubigerverbindung', label: 'Gläubiger' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

export default function PersonenPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('natuerlich');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Natürliche Personen
  const [natPersonen, setNatPersonen] = useState([]);
  const [rolleFilter, setRolleFilter] = useState('alle');
  const [sortByNat, setSortByNat] = useState('nachname_asc');
  
  // Juristische Personen
  const [jurPersonen, setJurPersonen] = useState([]);
  const [typFilter, setTypFilter] = useState('alle');
  const [sortByJur, setSortByJur] = useState('name_asc');
  
  // Statistiken
  const [stats, setStats] = useState({
    natuerliche_personen: 0,
    juristische_personen: 0,
    ansprechpartner: 0,
  });
  
  // Delete Dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, person: null, type: null });

  // Sortier-Funktionen
  const sortNatPersonen = (data) => {
    return [...data].sort((a, b) => {
      switch (sortByNat) {
        case 'nachname_asc': return (a.nachname || '').localeCompare(b.nachname || '', 'de');
        case 'nachname_desc': return (b.nachname || '').localeCompare(a.nachname || '', 'de');
        case 'vorname_asc': return (a.vorname || '').localeCompare(b.vorname || '', 'de');
        case 'vorname_desc': return (b.vorname || '').localeCompare(a.vorname || '', 'de');
        case 'rolle': return (a.rolle || '').localeCompare(b.rolle || '', 'de');
        default: return (a.nachname || '').localeCompare(b.nachname || '', 'de');
      }
    });
  };

  const sortJurPersonen = (data) => {
    return [...data].sort((a, b) => {
      switch (sortByJur) {
        case 'name_asc': return (a.name || '').localeCompare(b.name || '', 'de');
        case 'name_desc': return (b.name || '').localeCompare(a.name || '', 'de');
        case 'typ': return (a.typ || '').localeCompare(b.typ || '', 'de');
        default: return (a.name || '').localeCompare(b.name || '', 'de');
      }
    });
  };

  const loadNatuerlichePersonen = async () => {
    try {
      const params = {};
      if (rolleFilter !== 'alle') params.rolle = rolleFilter;
      if (searchTerm) params.search = searchTerm;
      
      const result = await betreuungAPI.listNatuerlichePersonen(params);
      setNatPersonen(sortNatPersonen(Array.isArray(result) ? result : []));
    } catch (error) {
      console.error('Fehler beim Laden natürlicher Personen:', error);
    }
  };

  const loadJuristischePersonen = async () => {
    try {
      const params = {};
      if (typFilter !== 'alle') params.typ = typFilter;
      if (searchTerm) params.search = searchTerm;
      
      const result = await betreuungAPI.listJuristischePersonen(params);
      setJurPersonen(sortJurPersonen(Array.isArray(result) ? result : []));
    } catch (error) {
      console.error('Fehler beim Laden juristischer Personen:', error);
    }
  };

  const loadStats = async () => {
    try {
      const result = await betreuungAPI.getPersonenStats();
      setStats(result);
    } catch (error) {
      console.error('Fehler beim Laden der Statistiken:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    await Promise.all([
      loadNatuerlichePersonen(),
      loadJuristischePersonen(),
      loadStats(),
    ]);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'natuerlich') {
        loadNatuerlichePersonen();
      } else {
        loadJuristischePersonen();
      }
    }, 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, rolleFilter, typFilter, activeTab, sortByNat, sortByJur]);

  const handleDelete = async () => {
    if (!deleteDialog.person) return;
    
    try {
      if (deleteDialog.type === 'natuerlich') {
        await betreuungAPI.deleteNatuerlichePerson(deleteDialog.person.id);
      } else {
        await betreuungAPI.deleteJuristischePerson(deleteDialog.person.id);
      }
      toast.success('Person gelöscht');
      setDeleteDialog({ open: false, person: null, type: null });
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const getRolleLabel = (rolle) => {
    return ROLLEN.find(r => r.value === rolle)?.label || rolle;
  };

  const getTypLabel = (typ) => {
    return TYPEN.find(t => t.value === typ)?.label || typ;
  };

  return (
    <div className="space-y-6" data-testid="personen-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Personenakten</h1>
          <p className="text-slate-500 mt-1">
            {stats.natuerliche_personen} Personen · {stats.juristische_personen} Institutionen
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => navigate('/personen/neu/natuerlich')}
          >
            <UserCircle className="w-4 h-4 mr-2" />
            Nat. Person
          </Button>
          <Button 
            onClick={() => navigate('/personen/neu/juristisch')}
          >
            <Building2 className="w-4 h-4 mr-2" />
            Institution
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="natuerlich" className="flex items-center gap-2">
            <UserCircle className="w-4 h-4" />
            Natürliche Personen
          </TabsTrigger>
          <TabsTrigger value="juristisch" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Institutionen
          </TabsTrigger>
        </TabsList>

        {/* Natürliche Personen Tab */}
        <TabsContent value="natuerlich" className="space-y-4">
          {/* Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Suche nach Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                </button>
              )}
            </div>
            <Select value={rolleFilter} onValueChange={setRolleFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Rolle" />
              </SelectTrigger>
              <SelectContent>
                {ROLLEN.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortByNat} onValueChange={setSortByNat}>
              <SelectTrigger className="w-full sm:w-44">
                <SelectValue placeholder="Sortierung" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nachname_asc">Nachname A-Z</SelectItem>
                <SelectItem value="nachname_desc">Nachname Z-A</SelectItem>
                <SelectItem value="vorname_asc">Vorname A-Z</SelectItem>
                <SelectItem value="vorname_desc">Vorname Z-A</SelectItem>
                <SelectItem value="rolle">Nach Rolle</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Liste */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : natPersonen.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {natPersonen.map((person) => (
                  <div
                    key={person.id}
                    className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <UserCircle className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 truncate">
                          {person.vorname} {person.nachname}
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                          <span className="px-2 py-0.5 bg-slate-100 rounded text-xs">
                            {getRolleLabel(person.rolle)}
                          </span>
                          {person.telefon && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {person.telefon}
                            </span>
                          )}
                          {person.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {person.email}
                            </span>
                          )}
                        </div>
                        {person.firma_id && (
                          <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                            <Briefcase className="w-3 h-3" /> Ansprechpartner bei Institution
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/personen/natuerlich/${person.id}`)}>
                          <Eye className="w-4 h-4 mr-2" />
                          Anzeigen
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/personen/natuerlich/${person.id}/bearbeiten`)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Bearbeiten
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => setDeleteDialog({ open: true, person, type: 'natuerlich' })}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Löschen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <UserCircle className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500 mb-4">Keine Personen gefunden</p>
                <Button onClick={() => navigate('/personen/neu/natuerlich')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Person anlegen
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Juristische Personen Tab */}
        <TabsContent value="juristisch" className="space-y-4">
          {/* Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Suche nach Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                </button>
              )}
            </div>
            <Select value={typFilter} onValueChange={setTypFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Typ" />
              </SelectTrigger>
              <SelectContent>
                {TYPEN.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortByJur} onValueChange={setSortByJur}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Sortierung" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name_asc">Name A-Z</SelectItem>
                <SelectItem value="name_desc">Name Z-A</SelectItem>
                <SelectItem value="typ">Nach Typ</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Liste */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : jurPersonen.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {jurPersonen.map((person) => (
                  <div
                    key={person.id}
                    className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-6 h-6 text-teal-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 truncate">
                          {person.name}
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                          <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs">
                            {getTypLabel(person.typ)}
                          </span>
                          {person.ort && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {person.ort}
                            </span>
                          )}
                          {person.telefon && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {person.telefon}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/personen/juristisch/${person.id}`)}>
                          <Eye className="w-4 h-4 mr-2" />
                          Anzeigen
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/personen/juristisch/${person.id}/bearbeiten`)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Bearbeiten
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Link className="w-4 h-4 mr-2" />
                          Ansprechpartner verwalten
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => setDeleteDialog({ open: true, person, type: 'juristisch' })}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Löschen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500 mb-4">Keine Institutionen gefunden</p>
                <Button onClick={() => navigate('/personen/neu/juristisch')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Institution anlegen
                </Button>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Info-Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-blue-800 text-sm">
          <strong>Vernetzung:</strong> Natürliche Personen können als Ansprechpartner bei Institutionen hinterlegt werden. 
          Maßnahmen (z.B. ALG1, Rente) verknüpfen Leistungsgeber mit Empfängern.
        </p>
      </div>

      {/* Inline Lösch-Bestätigung */}
      {deleteDialog.open && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-300 rounded-xl shadow-lg p-4 flex items-center gap-4 max-w-lg" data-testid="inline-delete-confirm">
          <p className="text-sm text-red-800">
            {deleteDialog.type === 'natuerlich'
              ? `${deleteDialog.person?.vorname} ${deleteDialog.person?.nachname}`
              : deleteDialog.person?.name
            } wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => setDeleteDialog({ open: false, person: null, type: null })}>Abbrechen</Button>
            <Button variant="destructive" size="sm" onClick={handleDelete}>Löschen</Button>
          </div>
        </div>
      )}
    </div>
  );
}
